import { useCallback, useEffect, useState } from 'react';
import {
  registerServiceWorker,
  sendSubscriptionToBackend,
  subscribeToPush,
  unsubscribeFromPush,
} from '@/lib/push';
import {
  clearPromptCooldown,
  detectPlatform,
  markPromptDismissed,
} from '@/lib/push-platform';

// ════════════════════════════════════════════════════════════════════
// usePushSubscription — single source of truth for "is push wired up
// end-to-end on this device?" Reports 'enabled' only when ALL of:
//   - platform is supported
//   - Notification.permission === 'granted'
//   - browser has an active PushSubscription
// Anything else (permission default/denied, no subscription, no SW) is
// 'disabled'. 'loading' covers the initial async checks; 'unsupported'
// covers platforms we can never serve push on.
//
// enable() is idempotent: safe to call when already enabled (returns
// the existing subscription path) or when permission was previously
// denied (request will fail, status stays 'disabled').
//
// All errors are swallowed and console.error'd — push UI must never
// break the surrounding page.
// ════════════════════════════════════════════════════════════════════

export type PushStatus = 'loading' | 'enabled' | 'disabled' | 'unsupported';

interface PushSubscriptionHook {
  status: PushStatus;
  enable: () => Promise<void>;
  disable: () => Promise<void>;
}

export function usePushSubscription(): PushSubscriptionHook {
  const [status, setStatus] = useState<PushStatus>('loading');

  // Recompute live state from browser APIs. Called on mount and after
  // enable/disable to keep the hook in sync with reality.
  const refresh = useCallback(async () => {
    const platform = detectPlatform();
    if (platform === 'unsupported') {
      setStatus('unsupported');
      return;
    }
    if (typeof Notification === 'undefined') {
      setStatus('unsupported');
      return;
    }
    if (Notification.permission !== 'granted') {
      setStatus('disabled');
      return;
    }
    // Permission granted — check we actually have a live subscription.
    try {
      const registration = await navigator.serviceWorker.getRegistration();
      if (!registration) {
        setStatus('disabled');
        return;
      }
      const sub = await registration.pushManager.getSubscription();
      setStatus(sub ? 'enabled' : 'disabled');
    } catch (err) {
      console.error('[push] refresh failed:', err);
      setStatus('disabled');
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const enable = useCallback(async () => {
    try {
      // 1. Ensure SW is registered. In production App.tsx already did
      //    this on mount; calling again is a no-op. In dev (where the
      //    Step 2 effect is gated off) this is the actual register call.
      const registration =
        (await navigator.serviceWorker?.getRegistration()) ??
        (await registerServiceWorker());
      if (!registration) {
        setStatus('disabled');
        return;
      }

      // 2. Ask the user. requestPermission can return synchronously or
      //    via Promise depending on browser; always await to normalize.
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        markPromptDismissed();
        setStatus('disabled');
        return;
      }

      // 3. Subscribe in the browser and ship the keys to the server.
      const subscription = await subscribeToPush(registration);
      if (!subscription) {
        setStatus('disabled');
        return;
      }
      await sendSubscriptionToBackend(subscription);

      clearPromptCooldown();
      setStatus('enabled');
    } catch (err) {
      console.error('[push] enable failed:', err);
      await refresh();
    }
  }, [refresh]);

  const disable = useCallback(async () => {
    try {
      await unsubscribeFromPush();
    } catch (err) {
      console.error('[push] disable failed:', err);
    } finally {
      await refresh();
    }
  }, [refresh]);

  return { status, enable, disable };
}
